/**
 * PreferenciasPorSource — Grid 18 sources x 4 channels (in-app/email/push/whatsapp).
 *
 * Spec docs/notificacoes/IMPLEMENTATION_SPEC_V1V2.md §3.4 #3 / §3.8 item 3.
 *
 * Desktop (>768px): Table-like grid com Switch por celula + Select min_severity por linha.
 * Mobile (<=768px): Accordion por source (uma row -> um item) com Switches + Select inline.
 */
import {
  Stack, Group, ThemeIcon, Text, Switch, Select, Box, Skeleton,
  Accordion, Table, ScrollArea,
} from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import { IconAdjustments } from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import {
  notificationPreferencesApi,
  type Subscription,
  type SubscriptionChannel,
  type SubscriptionSeverity,
  type SubscriptionUpdate,
} from '@/api/notification-preferences'
import { formatApiError } from '@/api/errors'
import { Callout, WidgetCard } from '@/components/ui'
import {
  PREFERENCE_CHANNELS,
  PREFERENCE_SOURCES,
  SEVERITY_OPTIONS,
  sourceLabel,
} from './preferencias-constants'

const QUERY_KEY = ['me', 'notification-subscriptions'] as const

type SourceMap = Record<string, Partial<Record<SubscriptionChannel, Subscription>>>

function buildSourceMap(items: Subscription[] | undefined): SourceMap {
  const map: SourceMap = {}
  if (!items) return map
  for (const s of items) {
    // Apenas subs "globais" do source (event_type null) sao usadas no grid.
    if (s.event_type != null && s.event_type !== '') continue
    if (!map[s.source]) map[s.source] = {}
    map[s.source][s.channel] = s
  }
  return map
}

function defaultSeverityForSource(map: SourceMap, src: string): SubscriptionSeverity {
  const buckets = map[src]
  if (!buckets) return 'info'
  for (const ch of PREFERENCE_CHANNELS) {
    const sub = buckets[ch.key]
    if (sub?.min_severity) return sub.min_severity
  }
  return 'info'
}

export function PreferenciasPorSource() {
  const isMobile = useMediaQuery('(max-width: 768px)')
  const qc = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => notificationPreferencesApi.listSubscriptions(),
  })

  const sourceMap = useMemo(() => buildSourceMap(data?.items), [data])

  const mutation = useMutation({
    mutationFn: (items: SubscriptionUpdate[]) =>
      notificationPreferencesApi.updateSubscriptions(items),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEY })
      notifications.show({
        color: 'green',
        title: 'Salvo',
        message: 'Preferência atualizada.',
      })
    },
    onError: (err: unknown) => {
      notifications.show({
        color: 'red',
        title: 'Erro',
        message: formatApiError(err, 'Falha ao atualizar preferência.'),
      })
    },
  })

  const handleToggle = (
    src: string,
    channel: SubscriptionChannel,
    enabled: boolean,
  ) => {
    const current = sourceMap[src]?.[channel]
    const min_severity = current?.min_severity ?? defaultSeverityForSource(sourceMap, src)
    mutation.mutate([
      {
        source: src,
        event_type: null,
        channel,
        enabled,
        min_severity,
      },
    ])
  }

  const handleSeverity = (src: string, sev: SubscriptionSeverity) => {
    // Atualiza min_severity em TODAS as subs daquele source (preserva enabled atual)
    const buckets = sourceMap[src] ?? {}
    const items: SubscriptionUpdate[] = PREFERENCE_CHANNELS.map((ch) => {
      const cur = buckets[ch.key]
      return {
        source: src,
        event_type: null,
        channel: ch.key,
        enabled: cur?.enabled ?? false,
        min_severity: sev,
      }
    })
    mutation.mutate(items)
  }

  return (
    <WidgetCard p="md">
      <Stack gap="sm">
        <Group gap={8} align="center">
          <ThemeIcon size={32} radius="md" color="blue" variant="light">
            <IconAdjustments size={18} />
          </ThemeIcon>
          <Text fw={600} size="sm">Preferências por fonte</Text>
        </Group>

        <Callout variant="info">
          <Text size="xs">
            Configure quais fontes notificam por cada canal. <strong>Severidade mínima</strong>
            {' '}filtra eventos abaixo do nível selecionado (ex: <code>warning</code> ignora
            {' '}<code>debug</code>/<code>info</code>).
          </Text>
        </Callout>

        {isLoading ? (
          <Stack gap="xs">
            <Skeleton h={32} />
            <Skeleton h={32} />
            <Skeleton h={32} />
            <Skeleton h={32} />
          </Stack>
        ) : isMobile ? (
          <Accordion variant="separated" radius="md">
            {PREFERENCE_SOURCES.map((src) => {
              const buckets = sourceMap[src] ?? {}
              const sev = defaultSeverityForSource(sourceMap, src)
              const enabledCount = PREFERENCE_CHANNELS.filter(
                (c) => buckets[c.key]?.enabled,
              ).length
              return (
                <Accordion.Item key={src} value={src}>
                  <Accordion.Control>
                    <Group justify="space-between" wrap="nowrap">
                      <Text size="sm" fw={500}>{sourceLabel(src)}</Text>
                      <Text size="xs" c="dimmed">{enabledCount}/{PREFERENCE_CHANNELS.length}</Text>
                    </Group>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Stack gap="xs">
                      {PREFERENCE_CHANNELS.map((ch) => (
                        <Group key={ch.key} justify="space-between">
                          <Text size="sm">{ch.label}</Text>
                          <Switch
                            checked={buckets[ch.key]?.enabled ?? false}
                            disabled={mutation.isPending}
                            onChange={(e) =>
                              handleToggle(src, ch.key, e.currentTarget.checked)
                            }
                            aria-label={`${sourceLabel(src)} ${ch.label}`}
                          />
                        </Group>
                      ))}
                      <Select
                        size="xs"
                        label="Severidade mínima"
                        data={SEVERITY_OPTIONS}
                        value={sev}
                        onChange={(v) =>
                          v && handleSeverity(src, v as SubscriptionSeverity)
                        }
                        allowDeselect={false}
                      />
                    </Stack>
                  </Accordion.Panel>
                </Accordion.Item>
              )
            })}
          </Accordion>
        ) : (
          <ScrollArea>
            <Box style={{ minWidth: 720 }}>
              <Table verticalSpacing="xs" horizontalSpacing="sm" highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ width: 180 }}>Fonte</Table.Th>
                    {PREFERENCE_CHANNELS.map((ch) => (
                      <Table.Th key={ch.key} style={{ textAlign: 'center', width: 90 }}>
                        {ch.shortLabel}
                      </Table.Th>
                    ))}
                    <Table.Th style={{ width: 140 }}>Severidade min.</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {PREFERENCE_SOURCES.map((src) => {
                    const buckets = sourceMap[src] ?? {}
                    const sev = defaultSeverityForSource(sourceMap, src)
                    return (
                      <Table.Tr key={src}>
                        <Table.Td>
                          <Text size="sm">{sourceLabel(src)}</Text>
                        </Table.Td>
                        {PREFERENCE_CHANNELS.map((ch) => (
                          <Table.Td key={ch.key} style={{ textAlign: 'center' }}>
                            <Switch
                              checked={buckets[ch.key]?.enabled ?? false}
                              disabled={mutation.isPending}
                              onChange={(e) =>
                                handleToggle(src, ch.key, e.currentTarget.checked)
                              }
                              aria-label={`${sourceLabel(src)} ${ch.label}`}
                              style={{ display: 'inline-flex' }}
                            />
                          </Table.Td>
                        ))}
                        <Table.Td>
                          <Select
                            size="xs"
                            data={SEVERITY_OPTIONS}
                            value={sev}
                            onChange={(v) =>
                              v && handleSeverity(src, v as SubscriptionSeverity)
                            }
                            allowDeselect={false}
                            comboboxProps={{ withinPortal: true }}
                          />
                        </Table.Td>
                      </Table.Tr>
                    )
                  })}
                </Table.Tbody>
              </Table>
            </Box>
          </ScrollArea>
        )}
      </Stack>
    </WidgetCard>
  )
}
